import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "src/store";
import { Stack } from "@mui/system";
import { thunks } from "src/thunks/suppliers";
import {
  CircularProgress,
  Box,
  Container,
  Autocomplete,
  TextField,
  Button,
} from "@mui/material";
import { withSuppliersGuard } from "src/hocs/with-role-based-guard.js";
import { useAuth } from "src/hooks/use-auth.js";
import { Seo } from "src/components/seo.js";
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import {
  columnsDefinition,
  dataGridStyle,
} from "src/sections/dashboard/suppliers/suppliers-table-settings";
import { groupTypes } from "src/guards/role-based-guard";
import { CustomTextField } from "src/components/inputs";
import moment from "moment";
import { MobileDatePicker } from "@mui/x-date-pickers";

const Suppliers = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();

  // User Group
  const userGroup = user.group?.key;

  const { suppliers, suppliersLoading } = useSelector(
    (state) => state.suppliers
  );

  // Filtering
  const [filters, setFilters] = useState({
    selectedId: null,
    selectedCode: null,
    selectedCompany: null,
    selectedEmail: null,
    selectedPortalAccess: null,
    selectedHasPassword: null,
    fromDate: null,
    toDate: null,
    allSuppliers: suppliers,
  });

  const applyFilters = (filters) => {
    const allSuppliers = suppliers.filter((supplier) => {
      return (
        (!filters.selectedId || supplier.supplierId === filters.selectedId) &&
        (!filters.selectedCode ||
          supplier.supplierCode?.includes(filters.selectedCode)) &&
        (!filters.selectedCompany ||
          supplier.companyName?.includes(filters.selectedCompany)) &&
        (!filters.selectedEmail ||
          supplier.email?.includes(filters.selectedEmail)) &&
        (!filters.selectedPortalAccess ||
          (supplier.portalAccess && filters.selectedPortalAccess === "Yes") ||
          (!supplier.portalAccess && filters.selectedPortalAccess === "No")) &&
        (!filters.selectedHasPassword ||
          (supplier.portalAccess && filters.selectedHasPassword === "Yes") ||
          (!supplier.portalAccess && filters.selectedHasPassword === "No")) &&
        (!filters.fromDate ||
          moment(filters.fromDate).format("YYYY/MM/DD HH:mm") <=
            moment(supplier.createdAt).format("YYYY/MM/DD HH:mm")) &&
        (!filters.toDate ||
          moment(filters.toDate).format("YYYY/MM/DD HH:mm") >=
            moment(supplier.createdAt).format("YYYY/MM/DD HH:mm"))
      );
    });

    setFilters((prevState) => ({
      ...prevState,
      ...filters,
      allSuppliers,
    }));
  };

  const handleFilterChange = (filterName, value) => {
    applyFilters({
      ...filters,
      [filterName]: value,
    });
  };

  // This function is used for the localStorage values retrieval
  const handleMultipleFiltersChange = (currentFilters) => {
    const filterObject = {
      ...filters,
    };

    for (let [key, value] of Object.entries(currentFilters)) {
      filterObject[key] = value;
    }

    applyFilters(filterObject);
  };

  const renderResetFiltersButton = () => {
    const [hoveredButton, setHoveredButton] = useState(false);

    const disableButton =
      !filters.selectedId &&
      !filters.selectedCode &&
      !filters.selectedCompany &&
      !filters.selectedEmail &&
      !filters.selectedPortalAccess &&
      !filters.selectedHasPassword &&
      !filters.fromDate &&
      !filters.toDate;

    const handleResetFilters = () => {
      handleMultipleFiltersChange({
        selectedId: null,
        selectedCode: null,
        selectedCompany: null,
        selectedEmail: null,
        selectedPortalAccess: null,
        selectedHasPassword: null,
        fromDate: null,
        toDate: null,
      });
    };

    return (
      <Button
        onMouseEnter={() => setHoveredButton(true)}
        onMouseLeave={() => setHoveredButton(false)}
        style={{
          fontFamily: "tmwCustomFont",
          textTransform: "uppercase",
          borderRadius: "25px",
          border: disableButton ? "1px solid #E6E6E6" : "1px solid red",
          transition: "ease-out 0.15s",
          letterSpacing: 2,
          fontSize: 11,
          userSelect: "none",
          fontWeight: 400,
          color: disableButton ? "#BEBEBE" : hoveredButton ? "white" : "red",
          background: disableButton ? "white" : hoveredButton ? "red" : "white",
        }}
        onClick={() => handleResetFilters()}
        disabled={disableButton}
        disableTouchRipple
      >
        Reset filters
      </Button>
    );
  };

  useEffect(() => {
    if (userGroup === groupTypes.tomorrowCrew) {
      dispatch(thunks.getSuppliers());
    }
  }, []);

  useEffect(() => {
    //
    // Filters
    //

    const savedIDFilters = JSON.parse(
      localStorage.getItem("suppliersIDFilters")
    );

    const savedCodeFilters = JSON.parse(
      localStorage.getItem("suppliersCodeFilters")
    );

    const savedCompanyFilters = JSON.parse(
      localStorage.getItem("suppliersCompanyFilters")
    );

    const savedEmailFilters = JSON.parse(
      localStorage.getItem("suppliersEmailFilters")
    );

    const savedPortalAccessFilters = JSON.parse(
      localStorage.getItem("suppliersPortalAccessFilters")
    );

    const savedHasPasswordFilters = JSON.parse(
      localStorage.getItem("suppliersHasPasswordFilters")
    );

    const savedFromFilters = JSON.parse(
      localStorage.getItem("suppliersFromDateFilters")
    );

    const savedToFilters = JSON.parse(
      localStorage.getItem("suppliersToDateFilters")
    );

    handleMultipleFiltersChange({
      selectedId: savedIDFilters,
      selectedCode: savedCodeFilters,
      selectedCompany: savedCompanyFilters,
      selectedEmail: savedEmailFilters,
      selectedPortalAccess: savedPortalAccessFilters,
      selectedHasPassword: savedHasPasswordFilters,
      fromDate: savedFromFilters,
      toDate: savedToFilters,
    });
  }, [suppliers]);

  useEffect(() => {
    // ID
    localStorage.setItem(
      "suppliersIDFilters",
      JSON.stringify(filters.selectedId)
    );

    // Code
    localStorage.setItem(
      "suppliersCodeFilters",
      JSON.stringify(filters.selectedCode)
    );

    // Company
    localStorage.setItem(
      "suppliersCompanyFilters",
      JSON.stringify(filters.selectedCompany)
    );

    // Email
    localStorage.setItem(
      "suppliersEmailFilters",
      JSON.stringify(filters.selectedEmail)
    );

    // Portal access
    localStorage.setItem(
      "suppliersPortalAccessFilters",
      JSON.stringify(filters.selectedPortalAccess)
    );

    // Has password
    localStorage.setItem(
      "suppliersPortalAccessFilters",
      JSON.stringify(filters.selectedHasPassword)
    );

    // From Date
    localStorage.setItem(
      "suppliersFromDateFilters",
      JSON.stringify(filters.fromDate)
    );

    // To Date
    localStorage.setItem(
      "suppliersToDateFilters",
      JSON.stringify(filters.toDate)
    );
  }, [filters]);

  const tableColumnsDefinition = columnsDefinition(
    groupTypes,
    userGroup,
    suppliers
  );

  const renderCircularProgress = () => {
    return (
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1100,
          color: "black",
        }}
      >
        <CircularProgress color="inherit" size={35} />
      </Box>
    );
  };

  const renderFiltersContainer = () => {
    return (
      userGroup === groupTypes.tomorrowCrew && (
        <Stack
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 25,
            margin: "-15px 0 15px 15px",
            width: "100%",
          }}
        >
          {renderIDFilter()}
          {renderCodeFilter()}
          {renderCompanyFilter()}
          {renderEmailFilter()}
          {renderPortalAccessFilter()}
          {renderHasPasswordFilter()}
          {renderFromDateAutoselect()}
          {renderToDateAutoselect()}
        </Stack>
      )
    );
  };

  const renderQuickSearch = () => {
    return (
      <Stack
        style={{
          flexDirection: "row",
          width: "200px",
        }}
      >
        <GridToolbarQuickFilter
          id="custom-searchbar"
          debounceMs={250}
          sx={{
            "& .MuiInput-root:before": {
              borderBottom: "none !important",
            },
            "& .MuiInput-root:after": {
              borderBottom: "none !important",
            },
            "& .MuiInput-underline:before": {
              borderBottom: "none !important",
            },
            "& .MuiSvgIcon-root": {
              cursor: "pointer",
            },
            "& .MuiInputBase-root-MuiInput-root": {
              cursor: "pointer",
            },
            "& .MuiInput-input": {
              fontFamily: "tmwCustomFont",
              paddingBottom: "2px",
              fontSize: 11,
            },
            "& .MuiInput-input::placeholder": {
              color: "#BEBEBE",
            },
            "& .MuiInput-input::-ms-input-placeholder": {
              color: "black",
            },
            overflow: "hidden",
          }}
          style={{
            fontFamily: "tmwCustomFont",
            borderRadius: "25px",
            border: "1px solid #E6E6E6",
            padding: "5px 10px",
            letterSpacing: 2,
            width: "200px",
            cursor: "pointer",
            transition: "all .2s ease-in-out",
          }}
          size="small"
          placeholder={"SEARCH"}
        />
      </Stack>
    );
  };

  const renderSecondaryButtons = () => {
    return (
      <Stack
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* LEFT Section */}
        <Stack style={{ flexDirection: "row", gap: 10 }}>
          {renderQuickSearch()}
        </Stack>

        {/* RIGHT Section */}
        <Stack style={{ flexDirection: "row", gap: 10 }}>
          {renderResetFiltersButton()}
        </Stack>
      </Stack>
    );
  };

  const customToolbar = () => {
    return (
      <GridToolbarContainer
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          margin: "0 -15px 15px 0",
        }}
      >
        <Stack
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            width: "100%",
          }}
        >
          <Stack
            style={{
              width: "100%",
              flexDirection: "row",
              justifyContent: "space-between",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "lightgrey transparent",
            }}
          >
            {renderFiltersContainer()}
          </Stack>
          <Stack
            style={{
              display: "flex",
              justifyContent: "space-around",
              minHeight: "100px",
              gap: 10,
              margin: "-15px 0 5px 0",
            }}
          >
            {renderSecondaryButtons()}
          </Stack>
        </Stack>
      </GridToolbarContainer>
    );
  };

  const renderTable = () => {
    return (
      <>
        {!suppliersLoading ? (
          <Container
            maxWidth={false}
            sx={{
              maxHeight: "75vh",
              width: "100%",
              scrollbarWidth: "thin",
              scrollbarColor: "lightgrey transparent",
            }}
          >
            <DataGridPro
              disableColumnFilter
              disableDensitySelector
              sx={dataGridStyle}
              rows={filters.allSuppliers}
              columns={tableColumnsDefinition}
              pagination
              slots={{
                toolbar: customToolbar,
              }}
              disableColumnSelector
              disableRowSelectionOnClick
              slotProps={{
                toolbar: {
                  style: {
                    marginTop: "-75px",
                    marginBottom: "25px",
                    marginLeft: 0,
                    display: "flex",
                    flexDirection: "row-reverse",
                    width: "25%",
                  },
                  csvOptions: { disableToolbarButton: true },
                  printOptions: { disableToolbarButton: true },
                },
                pagination: {
                  SelectProps: {
                    MenuProps: {
                      sx: {
                        "& .MuiMenuItem-root": {
                          textTransform: "uppercase",
                          fontSize: 11,
                          letterSpacing: 1,
                        },
                      },
                    },
                  },
                },
              }}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25, page: 0 },
                },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </Container>
        ) : (
          renderCircularProgress()
        )}
      </>
    );
  };

  //
  // Filters
  //
  const renderIDFilter = () => {
    let idAutocompleteData = [];

    const idValues = suppliers
      .map((supplier) => supplier.supplierId)
      .filter((id) => id);

    if (idValues.length) {
      idAutocompleteData = [...new Set(idValues)];
    }

    return (
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          margin: "30px 0 -15px 0",
        }}
      >
        <span
          style={{
            fontFamily: "tmwCustomFont",
            fontSize: 11,
            color: "black",
            letterSpacing: 3,
            fontWeight: 500,
            userSelect: "none",
            textTransform: "uppercase",
            marginLeft: "10px",
          }}
        >
          ID
        </span>
        <Autocomplete
          id="autocomplete-id"
          name="autocomplete-id"
          options={idAutocompleteData}
          value={filters.selectedId}
          onChange={(e, value) => {
            handleFilterChange("selectedId", value);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              <div
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 2,
                  fontSize: 11,
                  textTransform: "uppercase",
                  color: "black",
                }}
              >
                {option}
              </div>
            </li>
          )}
          renderInput={(params) => (
            <>
              <TextField
                {...params}
                sx={{
                  "& .MuiInput-underline:before": {
                    borderBottom: "1px solid #E6E6E6",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottom: "2px solid black",
                  },
                  "& .css-dk8gfa-MuiSvgIcon-root-MuiSelect-icon": {
                    cursor: "pointer",
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: "tmwCustomFont",
                    paddingLeft: "10px",
                    fontSize: 12,
                    textTransform: "uppercase",
                  },
                  padding: "10px 0",
                  width: "200px",
                }}
                id="id"
                name="id"
                fullWidth
                variant="standard"
              />
            </>
          )}
        />
      </Stack>
    );
  };

  const renderCodeFilter = () => {
    let codeAutocompleteData = [];

    const codeValues = suppliers
      .map((supplier) => supplier.supplierCode)
      .filter((code) => code);

    if (codeValues.length) {
      codeAutocompleteData = [...new Set(codeValues)];
    }

    return (
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          margin: "30px 0 -15px 0",
        }}
      >
        <span
          style={{
            fontFamily: "tmwCustomFont",
            fontSize: 11,
            color: "black",
            letterSpacing: 3,
            fontWeight: 500,
            userSelect: "none",
            textTransform: "uppercase",
            marginLeft: "10px",
          }}
        >
          Code
        </span>
        <Autocomplete
          id="autocomplete-code"
          name="autocomplete-code"
          options={codeAutocompleteData}
          value={filters.selectedCode}
          onChange={(e, value) => {
            handleFilterChange("selectedCode", value);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              <div
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 2,
                  fontSize: 11,
                  textTransform: "uppercase",
                  color: "black",
                }}
              >
                {option}
              </div>
            </li>
          )}
          renderInput={(params) => (
            <>
              <TextField
                {...params}
                sx={{
                  "& .MuiInput-underline:before": {
                    borderBottom: "1px solid #E6E6E6",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottom: "2px solid black",
                  },
                  "& .css-dk8gfa-MuiSvgIcon-root-MuiSelect-icon": {
                    cursor: "pointer",
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: "tmwCustomFont",
                    paddingLeft: "10px",
                    fontSize: 12,
                    textTransform: "uppercase",
                  },
                  padding: "10px 0",
                  width: "300px",
                }}
                id="code"
                name="code"
                fullWidth
                variant="standard"
              />
            </>
          )}
        />
      </Stack>
    );
  };

  const renderCompanyFilter = () => {
    let companyAutocompleteData = [];

    const companyValues = suppliers
      .map((supplier) => supplier.companyName)
      .filter((company) => company);

    if (companyValues.length) {
      companyAutocompleteData = [...new Set(companyValues)];
    }

    return (
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          margin: "30px 0 -15px 0",
        }}
      >
        <span
          style={{
            fontFamily: "tmwCustomFont",
            fontSize: 11,
            color: "black",
            letterSpacing: 3,
            fontWeight: 500,
            userSelect: "none",
            textTransform: "uppercase",
            marginLeft: "10px",
          }}
        >
          Company
        </span>
        <Autocomplete
          id="autocomplete-company"
          name="autocomplete-company"
          options={companyAutocompleteData}
          value={filters.selectedCompany}
          onChange={(e, value) => {
            handleFilterChange("selectedCompany", value);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              <div
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 2,
                  fontSize: 11,
                  textTransform: "uppercase",
                  color: "black",
                }}
              >
                {option}
              </div>
            </li>
          )}
          renderInput={(params) => (
            <>
              <TextField
                {...params}
                sx={{
                  "& .MuiInput-underline:before": {
                    borderBottom: "1px solid #E6E6E6",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottom: "2px solid black",
                  },
                  "& .css-dk8gfa-MuiSvgIcon-root-MuiSelect-icon": {
                    cursor: "pointer",
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: "tmwCustomFont",
                    paddingLeft: "10px",
                    fontSize: 12,
                    textTransform: "uppercase",
                  },
                  padding: "10px 0",
                  width: "300px",
                }}
                id="selectedCompany"
                name="selectedCompany"
                fullWidth
                variant="standard"
              />
            </>
          )}
        />
      </Stack>
    );
  };

  const renderEmailFilter = () => {
    let emailAutocompleteData = [];

    const emailValues = suppliers
      .map((supplier) => supplier.email)
      .filter((email) => email);

    if (emailValues.length) {
      emailAutocompleteData = [...new Set(emailValues)];
    }

    return (
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          margin: "30px 0 -15px 0",
        }}
      >
        <span
          style={{
            fontFamily: "tmwCustomFont",
            fontSize: 11,
            color: "black",
            letterSpacing: 3,
            fontWeight: 500,
            userSelect: "none",
            textTransform: "uppercase",
            marginLeft: "10px",
          }}
        >
          Email
        </span>
        <Autocomplete
          id="autocomplete-email"
          name="autocomplete-email"
          options={emailAutocompleteData}
          value={filters.selectedEmail}
          onChange={(e, value) => {
            handleFilterChange("selectedEmail", value);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              <div
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 2,
                  fontSize: 11,
                  textTransform: "uppercase",
                  color: "black",
                }}
              >
                {option}
              </div>
            </li>
          )}
          renderInput={(params) => (
            <>
              <TextField
                {...params}
                sx={{
                  "& .MuiInput-underline:before": {
                    borderBottom: "1px solid #E6E6E6",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottom: "2px solid black",
                  },
                  "& .css-dk8gfa-MuiSvgIcon-root-MuiSelect-icon": {
                    cursor: "pointer",
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: "tmwCustomFont",
                    paddingLeft: "10px",
                    fontSize: 12,
                    textTransform: "uppercase",
                  },
                  padding: "10px 0",
                  width: "300px",
                }}
                id="selectedEmail"
                name="selectedEmail"
                fullWidth
                variant="standard"
              />
            </>
          )}
        />
      </Stack>
    );
  };

  const renderPortalAccessFilter = () => {
    const portalAccessAutocompleteData = ["Yes", "No"];

    return (
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          margin: "30px 0 -15px 0",
        }}
      >
        <span
          style={{
            fontFamily: "tmwCustomFont",
            fontSize: 11,
            color: "black",
            letterSpacing: 3,
            fontWeight: 500,
            userSelect: "none",
            textTransform: "uppercase",
            marginLeft: "10px",
          }}
        >
          Portal Access
        </span>
        <Autocomplete
          id="autocomplete-portalAccess"
          name="autocomplete-portalAccess"
          options={portalAccessAutocompleteData}
          value={filters.selectedPortalAccess}
          onChange={(e, value) => {
            handleFilterChange("selectedPortalAccess", value);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              <div
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 2,
                  fontSize: 11,
                  textTransform: "uppercase",
                  color: "black",
                }}
              >
                {option}
              </div>
            </li>
          )}
          renderInput={(params) => (
            <>
              <TextField
                {...params}
                sx={{
                  "& .MuiInput-underline:before": {
                    borderBottom: "1px solid #E6E6E6",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottom: "2px solid black",
                  },
                  "& .css-dk8gfa-MuiSvgIcon-root-MuiSelect-icon": {
                    cursor: "pointer",
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: "tmwCustomFont",
                    paddingLeft: "10px",
                    fontSize: 12,
                    textTransform: "uppercase",
                  },
                  padding: "10px 0",
                  width: "200px",
                }}
                id="selectedPortalAccess"
                name="selectedPortalAccess"
                fullWidth
                variant="standard"
              />
            </>
          )}
        />
      </Stack>
    );
  };

  const renderHasPasswordFilter = () => {
    const hasPasswordAutocompleteData = ["Yes", "No"];

    return (
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          margin: "30px 0 -15px 0",
        }}
      >
        <span
          style={{
            fontFamily: "tmwCustomFont",
            fontSize: 11,
            color: "black",
            letterSpacing: 3,
            fontWeight: 500,
            userSelect: "none",
            textTransform: "uppercase",
            marginLeft: "10px",
          }}
        >
          Has Password
        </span>
        <Autocomplete
          id="autocomplete-hasPassword"
          name="autocomplete-hasPassword"
          options={hasPasswordAutocompleteData}
          value={filters.selectedHasPassword}
          onChange={(e, value) => {
            handleFilterChange("selectedHasPassword", value);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              <div
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 2,
                  fontSize: 11,
                  textTransform: "uppercase",
                  color: "black",
                }}
              >
                {option}
              </div>
            </li>
          )}
          renderInput={(params) => (
            <>
              <TextField
                {...params}
                sx={{
                  "& .MuiInput-underline:before": {
                    borderBottom: "1px solid #E6E6E6",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottom: "2px solid black",
                  },
                  "& .css-dk8gfa-MuiSvgIcon-root-MuiSelect-icon": {
                    cursor: "pointer",
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: "tmwCustomFont",
                    paddingLeft: "10px",
                    fontSize: 12,
                    textTransform: "uppercase",
                  },
                  padding: "10px 0",
                  width: "200px",
                }}
                id="selectedHasPassword"
                name="selectedHasPassword"
                fullWidth
                variant="standard"
              />
            </>
          )}
        />
      </Stack>
    );
  };

  const renderFromDateAutoselect = () => {
    return (
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          margin: "30px 0 -15px 0",
        }}
      >
        <span
          style={{
            fontFamily: "tmwCustomFont",
            fontSize: 11,
            color: "black",
            letterSpacing: 3,
            fontWeight: 500,
            userSelect: "none",
            textTransform: "uppercase",
            marginLeft: "10px",
          }}
        >
          From Date
        </span>
        <MobileDatePicker
          inputFormat="dd/MM/yyyy"
          onChange={(date) => {
            handleFilterChange("fromDate", date);
          }}
          renderInput={(inputProps) => (
            <>
              <CustomTextField
                style={{ width: "150px" }}
                // label={t(tokens.linesheets.table.windowStart)}w
                // onKeyDown={onKeyDown}
                inputProps={inputProps}
                calendar
                clearable={filters.fromDate}
                clearableClick={() => {
                  handleFilterChange("fromDate", null);
                }}
                // touched={formik.touched.inboundDelivery?.from}
                // errors={formik.errors.inboundDelivery?.from}
              />
            </>
          )}
          value={filters.fromDate}
          // maxDate={formik.values.inboundDelivery.to}
        />
      </Stack>
    );
  };

  const renderToDateAutoselect = () => {
    return (
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          margin: "30px -25px -15px 0",
        }}
      >
        <span
          style={{
            fontFamily: "tmwCustomFont",
            fontSize: 11,
            color: "black",
            letterSpacing: 3,
            fontWeight: 500,
            userSelect: "none",
            textTransform: "uppercase",
            marginLeft: "10px",
          }}
        >
          To Date
        </span>
        <MobileDatePicker
          inputFormat="dd/MM/yyyy"
          onChange={(date) => {
            handleFilterChange("toDate", date);
          }}
          renderInput={(inputProps) => (
            <>
              <CustomTextField
                style={{ width: "150px" }}
                // label={t(tokens.linesheets.table.windowStart)}w
                // onKeyDown={onKeyDown}
                inputProps={inputProps}
                calendar
                clearable={filters.toDate}
                clearableClick={() => {
                  handleFilterChange("toDate", null);
                }}
                // touched={formik.touched.inboundDelivery?.from}
                // errors={formik.errors.inboundDelivery?.from}
              />
            </>
          )}
          value={filters.toDate}
          // maxDate={formik.values.inboundDelivery.to}
        />
      </Stack>
    );
  };

  return (
    <>
      <Seo title="Dashboard: Suppliers" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 2,
          px: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {renderTable()}
      </Box>
    </>
  );
};

export default withSuppliersGuard(Suppliers);
